import {
  useEffect,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

/**
 * The primitive components.
 *
 * Kept in one file while the set is small: five components across five files
 * costs more navigation than it saves. Split when it stops fitting on a screen.
 *
 * Every one of these uses logical CSS properties (`inline-start`, `margin-inline`)
 * rather than physical ones, so Arabic and Urdu lay out correctly without a
 * second stylesheet.
 */

// --- Button ------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly loading?: boolean;
}

export function Button({
  variant = 'secondary',
  loading = false,
  disabled,
  children,
  className,
  ...rest
}: ButtonProps): ReactNode {
  return (
    <button
      {...rest}
      // A loading button is disabled so a double click cannot submit twice.
      disabled={disabled ?? loading}
      // Announced to screen readers, which otherwise get no signal that
      // something is in progress.
      aria-busy={loading || undefined}
      data-variant={variant}
      // Merged, not overridden: a caller-supplied class (e.g. `church-option`)
      // must land alongside `btn`, not replace it.
      className={className ? `btn ${className}` : 'btn'}
    >
      {loading && <span className="btn-spinner" aria-hidden="true" />}
      {children}
    </button>
  );
}

// --- Field -------------------------------------------------------------------

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly label: string;
  readonly error?: string;
  readonly hint?: string;
}

export function Field({ label, error, hint, id, required, ...rest }: FieldProps): ReactNode {
  const inputId = id ?? `field-${rest.name ?? label.replace(/\s+/g, '-').toLowerCase()}`;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  return (
    <div className="field">
      <label htmlFor={inputId} className="field-label">
        {label}
        {required && (
          <span className="field-required" aria-hidden="true">
            *
          </span>
        )}
      </label>

      {hint && (
        <p id={hintId} className="field-hint">
          {hint}
        </p>
      )}

      <input
        {...rest}
        id={inputId}
        required={required}
        aria-invalid={error ? true : undefined}
        // Both are linked, so a screen reader reads the hint and the error
        // rather than leaving the user to guess why the field was rejected.
        aria-describedby={[hint && hintId, error && errorId].filter(Boolean).join(' ') || undefined}
        className="field-input"
      />

      {error && (
        // `role="alert"` so the message is announced when it appears, not only
        // when the field is next focused.
        <p id={errorId} role="alert" className="field-error">
          {error}
        </p>
      )}
    </div>
  );
}

// --- ColorField ----------------------------------------------------------------

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

interface ColorFieldProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (hex: string) => void;
  readonly name?: string;
  readonly disabled?: boolean;
}

/**
 * A color swatch plus its hex value, kept in sync both ways — the native
 * color picker for browsing, the text field for pasting an exact brand hex.
 * Only ever calls `onChange` with a value matching `HEX_COLOR_PATTERN`: a
 * partially-typed hex (`"#2f"`) updates the text field's own display but
 * not the swatch or the caller's state, so an invalid mid-edit value never
 * reaches the live preview.
 */
export function ColorField({ label, value, onChange, name, disabled }: ColorFieldProps): ReactNode {
  const inputId = `field-${name ?? label.replace(/\s+/g, '-').toLowerCase()}`;
  const [text, setText] = useState(value);

  useEffect(() => setText(value), [value]);

  return (
    <div className="field color-field">
      <label htmlFor={inputId} className="field-label">
        {label}
      </label>
      <div className="color-field-row">
        <input
          type="color"
          id={inputId}
          value={HEX_COLOR_PATTERN.test(value) ? value : '#000000'}
          disabled={disabled}
          onChange={(event) => {
            setText(event.target.value);
            onChange(event.target.value);
          }}
        />
        <input
          type="text"
          className="field-input"
          value={text}
          disabled={disabled}
          dir="ltr"
          maxLength={7}
          onChange={(event) => {
            const next = event.target.value;
            setText(next);
            if (HEX_COLOR_PATTERN.test(next)) onChange(next);
          }}
        />
      </div>
    </div>
  );
}

// --- Status ------------------------------------------------------------------

/**
 * A status pill.
 *
 * `status` stays the raw enum because it drives the colour through
 * `data-status`, and a translated string cannot be a stable CSS selector. The
 * visible text comes from `label`, so the badge read "ACTIVE" in every language
 * until this took one.
 */
export function StatusBadge({ status, label }: { status: string; label: string }): ReactNode {
  return (
    <span className="badge" data-status={status}>
      {label}
    </span>
  );
}

// --- Switch --------------------------------------------------------------------

interface SwitchProps {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly label: string;
  /** Hides `label` visually while keeping it for screen readers — for a
   *  switch sitting next to its own visible heading, where repeating the
   *  name out loud would be redundant but an accessible name is still required. */
  readonly labelHidden?: boolean;
  readonly disabled?: boolean;
}

/**
 * A real toggle switch, not a checkbox — for a setting with an immediate,
 * binary effect (a provider is live or it is not) rather than a form field
 * that only takes effect on submit.
 */
export function Switch({
  checked,
  onChange,
  label,
  labelHidden = false,
  disabled = false,
}: SwitchProps): ReactNode {
  return (
    <label className="switch">
      <span className={labelHidden ? 'sr-only' : 'switch-label'}>{label}</span>
      <span className="switch-track" data-checked={checked || undefined}>
        <input
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className="switch-thumb" aria-hidden="true" />
      </span>
    </label>
  );
}

// --- Feedback ----------------------------------------------------------------

export function Alert({
  tone = 'danger',
  children,
}: {
  tone?: 'danger' | 'success' | 'warning';
  children: ReactNode;
}): ReactNode {
  return (
    <div className="alert" data-tone={tone} role={tone === 'danger' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }): ReactNode {
  return (
    <div className="empty">
      <p className="empty-title">{title}</p>
      {hint && <p className="empty-hint">{hint}</p>}
    </div>
  );
}

export function Spinner({ label }: { label: string }): ReactNode {
  return (
    <div className="spinner-wrap" role="status">
      <span className="spinner" aria-hidden="true" />
      {/* Visible to screen readers only: the animation conveys nothing to them. */}
      <span className="sr-only">{label}</span>
    </div>
  );
}
